import { useEffect } from 'react';
import { useStore } from 'react-redux';

import PORTAL_SELECTORS from '../constants/portalSelectors';

const FOCUSABLE_SELECTOR = 'input, button, textarea, select, [tabindex]';

/**
 * The item's controls in tab order.
 *
 * DOM order is safe to use here: every focusable inside an item row carries
 * `tabindex` 0 or -1, so nothing reorders the sequence. (ExercisePicker's
 * `tabIndex = 9999` would, but it lives outside the row — and we move focus
 * explicitly rather than predicting the browser's next target, so it can't
 * affect the wrap either way.)
 */
const getFocusablesIn = (root) => Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR))
  .filter((el) => {
    if (el.disabled) return false;
    if (el.getAttribute('tabindex') === '-1') return false;
    /** A collapsed saved table stays mounted under `display: none`, so its
     * inputs are still in the DOM but are not focusable. */
    if (el.offsetParent === null) return false;
    return true;
  });

/**
 * The item's entry field: the sets stepper, already where focus lands after an
 * auto-save. Falls back to the first focusable, which would be the row's
 * selection checkbox.
 */
const focusEntryField = (row, focusables) => {
  const entry = row.querySelector('[data-focus-target]') || focusables[0];
  if (entry) entry.focus();
};

/**
 * Keeps Tab inside the active calendar item instead of letting focus escape.
 *
 * Reaching the last field of an open exercise used to hand focus to whatever
 * came next in the document, so a coach working through one item had to click
 * back in. Tab now returns to the item's entry field.
 *
 * The active exercise owns Tab. Only that item's hook acts, and it also pulls
 * focus in when the keyboard is stranded in a different item: clicking a row
 * makes it active without moving DOM focus, because react-beautiful-dnd
 * preventDefaults the mousedown once it takes a drag lock. Without the pull-in,
 * Tab would keep walking the item the coach clicked away from.
 *
 * Forward only, by design: Shift+Tab still steps out, so a keyboard-only coach
 * is never stuck in an item, and month view's popover keeps its documented
 * "Shift+Tab steps back out" behavior.
 *
 * @param {React.RefObject} containerRef ref to the item row — the one node that
 *   holds all of an item's focusable controls (checkbox, chrome, and table).
 * @param {boolean} enabled false opts an item out entirely.
 * @param {boolean} isActiveItem whether this row is the active exercise.
 */
const useTabCyclesWithinItem = (containerRef, enabled = true, isActiveItem = true) => {
  const store = useStore();

  useEffect(() => {
    if (!enabled || !isActiveItem) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      /** Shift+Tab is the way out. */
      if (e.shiftKey) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const row = containerRef.current;
      if (!row) return;

      /** Portaled widgets own Tab while they are open. The superset popover
       * moves real focus into a body-portaled listbox, so the containment check
       * below already excludes it; this covers the widgets that keep focus on a
       * trigger inside the row while their menu is elsewhere. */
      if (e.target.closest?.(PORTAL_SELECTORS)) return;
      /** An open react-select menu: Tab there picks the focused option. Its
       * menu portals to document.body, so this has to be a global query. */
      if (document.querySelector('[class*="__menu"]')) return;

      /** react-modal runs its own focus trap — two would fight over the key. */
      const { calendar } = store.getState();
      if (calendar.data.activeModal || calendar.data.activeSecondLayerModal) return;
      if (calendar.data.dragIsInProgress) return;

      const focusables = getFocusablesIn(row);

      if (!row.contains(e.target)) {
        /** Focus is stranded in the item the coach clicked away from. Bring it
         * into the active item rather than letting Tab walk the old one — but
         * only when it is stranded in *some* item: Tab in the sidebar, day
         * header or anywhere else outside the calendar is not ours to take. */
        const focusedItem = e.target.closest?.('[data-exercise-item]');
        if (!focusedItem) return;
        /** A read-only item (inherited, athlete-added) can't become active, so
         * focus in it isn't stranded - the coach tabbed or clicked there on
         * purpose. Let the browser's own Tab order run. */
        if (focusedItem.hasAttribute('data-read-only-item')) return;
        if (!focusables.length) return;
        e.preventDefault();
        focusEntryField(row, focusables);
        return;
      }

      /** A single focusable is a collapsed row whose only control is the
       * checkbox. Wrapping that to itself would strand the coach on it. */
      if (focusables.length < 2) return;
      if (e.target !== focusables[focusables.length - 1]) return;

      e.preventDefault();
      focusEntryField(row, focusables);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [containerRef, enabled, isActiveItem, store]);
};

export default useTabCyclesWithinItem;
